/**
 * Task-specific Column Manager Hook
 *
 * Thin wrapper around the generic useColumnManager from common/DataTable.
 * Adds vertical-awareness for default columns.
 *
 * Architecture:
 * - Uses shared hook from common/DataTable
 * - Injects vertical-specific getDefaultColumns function
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_COLUMN_URL_PARAM,
  parseColumnsFromUrl,
  useColumnManager as useGenericColumnManager,
  type UseColumnManagerReturn,
} from '@/components/common/DataTable';
import { TableColumn } from '@/components/common/DataTable/types';
import { isAgentColumnAlwaysVisibleForUser } from '@/lib/featureFlags';
import { selectUser } from '@/state/selectors/root-selectors';

import {
  getDefaultColumnsForVertical,
  insertAgentColumnNearTaskName,
} from '../config/columnConfig';
import { TableColumnIds } from '../config/filterConfig';

const TASK_COLUMNS_VERSION_KEY = 'cols_v';
const TASK_COLUMNS_VERSION = '6';

const migrateAgentColumns = (columns: string[], shouldIncludeAgent: boolean): string[] => {
  if (!shouldIncludeAgent && !columns.includes(TableColumnIds.AGENT)) {
    return columns;
  }

  return insertAgentColumnNearTaskName(
    shouldIncludeAgent ? columns : columns.filter((columnId) => columnId !== TableColumnIds.AGENT),
  );
};

// ----------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------

export interface UseColumnManagerOptions<TData> {
  /** All available columns from the column factory */
  allColumns: TableColumn<TData>[];
  /** Vertical ID for default columns lookup */
  verticalId: string;
}

// Re-export the return type from the generic hook
export type { UseColumnManagerReturn };

// ----------------------------------------------------------------------------
// Hook Implementation
// ----------------------------------------------------------------------------

export function useColumnManager<TData>({
  allColumns,
  verticalId,
}: UseColumnManagerOptions<TData>): UseColumnManagerReturn<TData> {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector(selectUser);
  const shouldAlwaysShowAgentColumn = isAgentColumnAlwaysVisibleForUser(currentUser?.email);
  const shouldIncludeAgentColumnForVertical =
    verticalId === 'claims' || verticalId === 'medical';

  // Create a stable getDefaultColumns function based on verticalId
  const getDefaultColumns = useCallback(
    () => {
      const defaultColumns = getDefaultColumnsForVertical(verticalId);
      if (shouldIncludeAgentColumnForVertical || shouldAlwaysShowAgentColumn) {
        return insertAgentColumnNearTaskName(defaultColumns);
      }
      return defaultColumns;
    },
    [shouldAlwaysShowAgentColumn, shouldIncludeAgentColumnForVertical, verticalId],
  );

  const hasAgentColumn = useMemo(
    () => allColumns.some((column) => column.id === TableColumnIds.AGENT),
    [allColumns],
  );

  const requiredColumns = useMemo(() => {
    if (
      !hasAgentColumn ||
      !shouldAlwaysShowAgentColumn
    ) {
      return [];
    }

    return [TableColumnIds.AGENT];
  }, [hasAgentColumn, shouldAlwaysShowAgentColumn]);

  const shouldMigrateAgentColumns = useMemo(
    () =>
      hasAgentColumn &&
      (shouldIncludeAgentColumnForVertical || shouldAlwaysShowAgentColumn),
    [
      hasAgentColumn,
      shouldAlwaysShowAgentColumn,
      shouldIncludeAgentColumnForVertical,
    ],
  );

  // Delegate to the generic hook
  const columnManager = useGenericColumnManager({
    allColumns,
    getDefaultColumns,
    requiredColumns,
    layoutVersion:
      shouldMigrateAgentColumns
        ? {
            key: TASK_COLUMNS_VERSION_KEY,
            value: TASK_COLUMNS_VERSION,
          }
        : undefined,
  });

  const agentColumnMigration = useMemo(() => {
    if (!shouldMigrateAgentColumns) {
      return null;
    }

    if (searchParams.get(TASK_COLUMNS_VERSION_KEY) === TASK_COLUMNS_VERSION) {
      return null;
    }

    const parsedColumns = parseColumnsFromUrl(searchParams.get(DEFAULT_COLUMN_URL_PARAM));
    if (!parsedColumns) {
      return null;
    }

    const migratedColumns = migrateAgentColumns(
      parsedColumns,
      shouldIncludeAgentColumnForVertical || shouldAlwaysShowAgentColumn,
    );

    if (migratedColumns.join(',') === parsedColumns.join(',')) {
      return null;
    }

    return migratedColumns;
  }, [
    searchParams,
    shouldAlwaysShowAgentColumn,
    shouldIncludeAgentColumnForVertical,
    shouldMigrateAgentColumns,
  ]);

  useEffect(() => {
    if (!agentColumnMigration) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set(DEFAULT_COLUMN_URL_PARAM, agentColumnMigration.join(','));
        next.set(TASK_COLUMNS_VERSION_KEY, TASK_COLUMNS_VERSION);
        return next;
      },
      { replace: true },
    );
  }, [agentColumnMigration, setSearchParams]);

  return columnManager;
}
